import { useState, useEffect } from "react";
import { supabase, type Stat } from "@/lib/supabase";

export default function StatsManager() {
  const [statsList, setStatsList] = useState<Stat[]>([]);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({ label: "", value: 0, display_order: 1 });

  useEffect(() => { fetchStats(); }, []);

  const fetchStats = async () => {
    setIsLoading(true);
    const { data } = await supabase.from("stats").select("*").order("display_order", { ascending: true });
    setStatsList(data || []);
    setIsLoading(false);
  };

  const handleEdit = (stat: Stat) => {
    setFormData({ label: stat.label, value: stat.value, display_order: stat.display_order });
    setEditingId(stat.id);
    setShowModal(true);
  };

  const handleSave = async () => {
    setSaving(true);
    if (editingId) {
      const { error } = await supabase.from("stats").update(formData).eq("id", editingId);
      if (!error) await fetchStats();
    } else {
      const { error } = await supabase.from("stats").insert(formData);
      if (!error) await fetchStats();
    }
    setSaving(false);
    setShowModal(false);
    setEditingId(null);
    setFormData({ label: "", value: 0, display_order: 1 });
  };

  const handleDelete = async (id: string) => {
    if (!confirm("Are you sure?")) return;
    const { error } = await supabase.from("stats").delete().eq("id", id);
    if (!error) await fetchStats();
  };

  const sorted = [...statsList].sort((a, b) => a.display_order - b.display_order);

  if (isLoading) {
    return (
      <div className="p-4 md:p-6 lg:p-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-48" />
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {Array.from({ length: 4 }).map((_, i) => (<div key={i} className="h-32 bg-gray-100 rounded-xl" />))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 lg:p-8">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <h1 className="font-heading font-bold text-2xl text-primary-900">Statistics Management</h1>
        <button onClick={() => { setEditingId(null); setFormData({ label: "", value: 0, display_order: statsList.length + 1 }); setShowModal(true); }} className="inline-flex items-center gap-2 px-4 py-2.5 bg-gold-600 hover:bg-gold-700 text-white text-sm font-medium rounded-lg transition-colors whitespace-nowrap">
          <i className="ri-add-line" /> Add Statistic
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {sorted.map((stat) => (
          <div key={stat.id} className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 hover:shadow-md transition-all">
            <div className="flex items-start justify-between mb-3">
              <div className="w-12 h-12 rounded-xl bg-primary-50 flex items-center justify-center text-primary-600">
                <i className="ri-bar-chart-box-line text-xl" />
              </div>
              <div className="flex gap-1">
                <button onClick={() => handleEdit(stat)} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 hover:text-primary-600 transition-colors"><i className="ri-edit-line text-xs" /></button>
                <button onClick={() => handleDelete(stat.id)} className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 transition-colors"><i className="ri-delete-bin-line text-xs" /></button>
              </div>
            </div>
            <p className="font-heading text-2xl font-bold text-primary-900">{stat.value}+</p>
            <p className="text-gray-500 text-sm mt-1">{stat.label}</p>
            <p className="text-gray-300 text-xs mt-2">Order: {stat.display_order}</p>
          </div>
        ))}
      </div>

      {showModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
              <h2 className="font-heading font-bold text-lg text-primary-900">{editingId ? "Edit Statistic" : "Add Statistic"}</h2>
              <button onClick={() => setShowModal(false)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-500"><i className="ri-close-line" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Label</label>
                <input type="text" value={formData.label} onChange={(e) => setFormData({ ...formData, label: e.target.value })} className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-gold-500 focus:ring-1 focus:ring-gold-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Value</label>
                <input type="number" value={formData.value} onChange={(e) => setFormData({ ...formData, value: parseInt(e.target.value) || 0 })} className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-gold-500 focus:ring-1 focus:ring-gold-500" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Display Order</label>
                <input type="number" value={formData.display_order} onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 1 })} className="w-full px-3 py-2.5 rounded-lg border border-gray-200 text-sm focus:outline-none focus:border-gold-500 focus:ring-1 focus:ring-gold-500" />
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
              <button onClick={() => setShowModal(false)} className="px-4 py-2.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">Cancel</button>
              <button onClick={handleSave} disabled={saving} className="px-5 py-2.5 bg-gold-600 hover:bg-gold-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-60">{saving ? "Saving..." : "Save"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}